/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

var postVaultMessage = function (target, message, origin) {
    target === null || target === void 0 ? void 0 : target.postMessage(__assign(__assign({}, message), { service: "AprilVault" }), origin !== null && origin !== void 0 ? origin : "*");
};
var addVaultMessageListener = function (sourceCallback, callback) {
    var messageListener = function (event) {
        var _a;
        var source = sourceCallback();
        if (!source || event.source !== source || typeof event.data !== "object")
            return;
        ((_a = event.data) === null || _a === void 0 ? void 0 : _a.service) === "AprilVault" && callback(event.data, event);
    };
    window.addEventListener("message", messageListener);
    return function () { return window.removeEventListener("message", messageListener); };
};
var createVaultIframe = function (_a) {
    var fieldType = _a.fieldType;
    var iframeTitle = fieldType === "cardNumber"
        ? "Secure card number input frame"
        : fieldType === "expiryDate"
            ? "Secure card expiration date input frame"
            : fieldType === "cardCvc"
                ? "Secure card security code input frame"
                : "Secure input frame";
    var iframe = document.createElement("iframe");
    iframe.title = iframeTitle;
    iframe.frameBorder = "0";
    iframe.scrolling = "0";
    iframe.style.width = "100%";
    iframe.style.height = "100%";
    return iframe;
};
var getDefaultFieldState = function () { return ({
    isDirty: false,
    isEmpty: true,
    isValid: false,
    isPossible: true,
}); };
var validateVault = function (vault) {
    var _a, _b, _c;
    return {
        isValid: Object.values(vault.fields).every(function (_a) {
            var state = _a.state;
            return state.isValid;
        }),
        errors: {
            cardNumber: !((_a = vault.fields.cardNumber) === null || _a === void 0 ? void 0 : _a.state.isValid)
                ? "Invalid card number"
                : undefined,
            expiryDate: !((_b = vault.fields.expiryDate) === null || _b === void 0 ? void 0 : _b.state.isValid)
                ? "Invalid expiration date"
                : undefined,
            cardCvc: !((_c = vault.fields.cardCvc) === null || _c === void 0 ? void 0 : _c.state.isValid)
                ? "Invalid security code"
                : undefined,
        },
    };
};
var submitVault = function (vault, submitRequest) {
    var field = Object.values(vault.fields)[0];
    if (!field)
        throw Error("Vault fields not found");
    var promise = new Promise(function (resolve) {
        var removeMessageListener = addVaultMessageListener(function () { return field.iframe.contentWindow; }, function (message) {
            if (message.type === "SubmitResponse") {
                resolve(message.submitResponse);
                removeMessageListener();
            }
        });
    });
    postVaultMessage(field.iframe.contentWindow, {
        type: "SubmitRequest",
        submitRequest: submitRequest,
    });
    return promise;
};
var handleVaultSubmitResponse = function (submitResponse) {
    return Promise.resolve(submitResponse)
        .then(function (_a) {
        var response = _a.response, error = _a.error;
        if (!response)
            throw error;
        return response;
    })
        .then(function (response) {
        try {
            var json = JSON.parse(response.body);
            return { response: response, json: json };
        }
        catch (error) {
            throw response.body;
        }
    })
        .then(function (_a) {
        var status = _a.response.status, json = _a.json;
        if (status < 200 || status > 299)
            throw json;
        return json;
    });
};

let nanoid = (size = 21) =>
  crypto.getRandomValues(new Uint8Array(size)).reduce((id, byte) => {
    byte &= 63;
    if (byte < 36) {
      id += byte.toString(36);
    } else if (byte < 62) {
      id += (byte - 26).toString(36).toUpperCase();
    } else if (byte > 62) {
      id += '-';
    } else {
      id += '_';
    }
    return id
  }, '');

var createVaultField = function (vaultHost, vaultId, fieldOptions) {
    var elementId = fieldOptions.elementId, fieldType = fieldOptions.fieldType, fieldStyles = fieldOptions.fieldStyles, inputProps = fieldOptions.inputProps, onReady = fieldOptions.onReady, onEvent = fieldOptions.onEvent, onError = fieldOptions.onError;
    var container = window.document.getElementById(elementId);
    if (!container)
        throw Error("elementId not found: ".concat(elementId));
    var iframe = createVaultIframe({ fieldType: fieldType });
    var promise = new Promise(function (resolve, reject) {
        var removeMessageListener = addVaultMessageListener(function () { return iframe.contentWindow; }, function (message) {
            if (message.type === "Error") {
                onError === null || onError === void 0 ? void 0 : onError({
                    errorMessage: message.errorMessage,
                    field: field,
                });
            }
            if (message.type === "Ready") {
                postVaultMessage(iframe.contentWindow, {
                    type: "Config",
                    fieldConfig: {
                        vaultId: vaultId,
                        fieldType: fieldType,
                        inputProps: inputProps,
                        fieldStyles: fieldStyles,
                    },
                });
                resolve();
            }
            if (message.type === "Event") {
                field.state = message.fieldState;
                onEvent === null || onEvent === void 0 ? void 0 : onEvent({
                    eventName: message.eventName,
                    field: field,
                });
            }
        });
        iframe.onerror = function () {
            reject("Failed to load frame: ".concat(fieldType));
            removeMessageListener();
        };
    }).then(onReady);
    iframe.src = vaultHost;
    container.appendChild(iframe);
    var field = {
        type: fieldType,
        state: getDefaultFieldState(),
        iframe: iframe,
        promise: promise,
    };
    return field;
};
var createVault = function (vaultOptions) {
    var vaultHost = vaultOptions.vaultHost;
    var vaultId = "vault-".concat(nanoid());
    var fields = {};
    var cardType = null;
    if (vaultOptions.fields.cardNumber) {
        var fieldType_1 = "cardNumber";
        fields[fieldType_1] = createVaultField(vaultHost, vaultId, __assign(__assign({}, vaultOptions.fields[fieldType_1]), { fieldType: fieldType_1, inputProps: __assign({ placeholder: "Card number", autoComplete: "cc-number", "aria-label": "Card Number Input" }, vaultOptions.fields[fieldType_1].inputProps), onEvent: function (event) {
                var _a, _b, _c, _d;
                if (event.field.state.cardType !== cardType) {
                    cardType = (_a = event.field.state.cardType) !== null && _a !== void 0 ? _a : null;
                    if (fields.cardCvc) {
                        postVaultMessage(fields.cardCvc.iframe.contentWindow, {
                            type: "CardType",
                            cardType: cardType,
                        });
                    }
                }
                (_c = (_b = vaultOptions.fields[fieldType_1]) === null || _b === void 0 ? void 0 : _b.onEvent) === null || _c === void 0 ? void 0 : _c.call(_b, event);
                (_d = vaultOptions.onEvent) === null || _d === void 0 ? void 0 : _d.call(vaultOptions, __assign(__assign({}, event), { vault: vault }));
            }, onError: function (error) {
                var _a, _b, _c;
                (_b = (_a = vaultOptions.fields[fieldType_1]) === null || _a === void 0 ? void 0 : _a.onError) === null || _b === void 0 ? void 0 : _b.call(_a, error);
                (_c = vaultOptions.onError) === null || _c === void 0 ? void 0 : _c.call(vaultOptions, __assign(__assign({}, error), { vault: vault }));
            } }));
    }
    if (vaultOptions.fields.expiryDate) {
        var fieldType_2 = "expiryDate";
        fields[fieldType_2] = createVaultField(vaultHost, vaultId, __assign(__assign({}, vaultOptions.fields[fieldType_2]), { fieldType: fieldType_2, inputProps: __assign({ placeholder: "MM / YY", autoComplete: "cc-exp", "aria-label": "Card expiration date" }, vaultOptions.fields[fieldType_2].inputProps), onEvent: function (event) {
                var _a, _b, _c;
                (_b = (_a = vaultOptions.fields[fieldType_2]) === null || _a === void 0 ? void 0 : _a.onEvent) === null || _b === void 0 ? void 0 : _b.call(_a, event);
                (_c = vaultOptions.onEvent) === null || _c === void 0 ? void 0 : _c.call(vaultOptions, __assign(__assign({}, event), { vault: vault }));
            }, onError: function (error) {
                var _a, _b, _c;
                (_b = (_a = vaultOptions.fields[fieldType_2]) === null || _a === void 0 ? void 0 : _a.onError) === null || _b === void 0 ? void 0 : _b.call(_a, error);
                (_c = vaultOptions.onError) === null || _c === void 0 ? void 0 : _c.call(vaultOptions, __assign(__assign({}, error), { vault: vault }));
            } }));
    }
    if (vaultOptions.fields.cardCvc) {
        var fieldType_3 = "cardCvc";
        fields[fieldType_3] = createVaultField(vaultHost, vaultId, __assign(__assign({}, vaultOptions.fields[fieldType_3]), { fieldType: fieldType_3, inputProps: __assign({ placeholder: "CVC", autoComplete: "cc-csc", "aria-label": "Card Security Code" }, vaultOptions.fields[fieldType_3].inputProps), onEvent: function (event) {
                var _a, _b, _c;
                (_b = (_a = vaultOptions.fields[fieldType_3]) === null || _a === void 0 ? void 0 : _a.onEvent) === null || _b === void 0 ? void 0 : _b.call(_a, event);
                (_c = vaultOptions.onEvent) === null || _c === void 0 ? void 0 : _c.call(vaultOptions, __assign(__assign({}, event), { vault: vault }));
            }, onError: function (error) {
                var _a, _b, _c;
                (_b = (_a = vaultOptions.fields[fieldType_3]) === null || _a === void 0 ? void 0 : _a.onError) === null || _b === void 0 ? void 0 : _b.call(_a, error);
                (_c = vaultOptions.onError) === null || _c === void 0 ? void 0 : _c.call(vaultOptions, __assign(__assign({}, error), { vault: vault }));
            } }));
    }
    var promise = Promise.all(Object.values(fields).map(function (_a) {
        var promise = _a.promise;
        return promise;
    })).then(vaultOptions.onReady);
    var vault = {
        fields: fields,
        cardType: cardType,
        submit: function (submitRequest) { return submitVault(vault, submitRequest); },
        validate: function () { return validateVault(vault); },
        promise: promise,
    };
    return vault;
};

export { addVaultMessageListener, createVault, createVaultField, createVaultIframe, getDefaultFieldState, handleVaultSubmitResponse, postVaultMessage, submitVault, validateVault };
